import axios from "axios";
import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TEventListItem,
  TEventPayload,
  TEventRecurrencePayload,
  TFindTimesPayload,
  TFindTimesResponse,
  TOccurrenceDeletePayload,
  TOccurrenceEditPayload,
} from "@/types/event";
import type { TIssue } from "@/types/issue";

/**
 * Result of editing a series' pattern (NG-810). The backend warns before
 * dropping customized/cancelled occurrences the new rule no longer generates:
 * `needs_confirm` carries that count so the caller can confirm and retry.
 */
export type TEditSeriesResult =
  | { status: "ok"; event: TEventListItem }
  | { status: "needs_confirm"; orphanedCount: number };

interface TSubWorkItemsResponse {
  sub_issues: TIssue[];
  state_distribution: Record<string, string[]>;
}

export interface TEventSessionPayload {
  name: string;
  event_detail: { start_datetime: string; end_datetime: string; all_day?: boolean };
}

export class EventService extends ApiService {
  async getEvent(
    slug: string,
    eventId: string,
    config?: ApiRequestConfig,
  ): Promise<TEventListItem> {
    return this.get(`/api/workspaces/${slug}/events/${eventId}/`, config);
  }

  async createEvent(slug: string, data: TEventPayload): Promise<TEventListItem> {
    return this.post(`/api/workspaces/${slug}/events/`, data);
  }

  async updateEvent(
    slug: string,
    eventId: string,
    data: Partial<TEventPayload>,
  ): Promise<TEventListItem> {
    return this.patch(`/api/workspaces/${slug}/events/${eventId}/`, data);
  }

  async deleteEvent(slug: string, eventId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/events/${eventId}/`);
  }

  // --- Recurring-event occurrence operations (NG-396 → NG-392 endpoints) -----
  // ``recurrence_scope`` selects the calendar semantics on the backend: "this"
  // edits/cancels a single occurrence; "this_and_following" splits / deletes
  // forward. The occurrence is addressed by its ``recurrence_id`` (original start).

  async editOccurrence(
    slug: string,
    seriesId: string,
    data: TOccurrenceEditPayload,
  ): Promise<TEventListItem> {
    return this.patch(`/api/workspaces/${slug}/events/series/${seriesId}/occurrences/`, data);
  }

  async deleteOccurrence(
    slug: string,
    seriesId: string,
    data: TOccurrenceDeletePayload,
  ): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/events/series/${seriesId}/occurrences/`, { data });
  }

  async deleteSeries(slug: string, seriesId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/events/series/${seriesId}/`);
  }

  /**
   * Edit a recurring series' pattern in place (NG-810). Without `confirm` the
   * backend 409s if the new rule would orphan customized/cancelled occurrences;
   * that surfaces as `needs_confirm` with the count so the caller can warn and
   * retry with `confirm: true`. Any other error propagates.
   */
  async editSeries(
    slug: string,
    seriesId: string,
    data: { rrule: string; confirm?: boolean },
  ): Promise<TEditSeriesResult> {
    try {
      const event = await this.patch<TEventListItem>(
        `/api/workspaces/${slug}/events/series/${seriesId}/`,
        data,
      );
      return { status: "ok", event };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const count = error.response.data?.orphaned_override_count;
        if (typeof count === "number" && Number.isInteger(count) && count > 0) {
          return { status: "needs_confirm", orphanedCount: count };
        }
        // A 409 without a valid positive integer count is not the warn contract —
        // surface it rather than converting it into a confirmable no-op.
        console.warn("editSeries got a 409 without a valid orphan count", {
          slug,
          seriesId,
          data: error.response.data,
        });
      }
      throw error;
    }
  }

  /**
   * Attach a recurrence to an existing one-off event (NG-804): the event becomes
   * the series master and its own first occurrence, seeded from its schedule.
   * The backend 409s if the event already recurs. Returns the updated event
   * (now carrying `is_recurring` / `series_id` / `recurrence_rule`).
   */
  async attachSeries(
    slug: string,
    eventId: string,
    data: TEventRecurrencePayload,
  ): Promise<TEventListItem> {
    return this.post(`/api/workspaces/${slug}/events/${eventId}/recurrence/`, data);
  }

  async findAvailableTimes(slug: string, data: TFindTimesPayload): Promise<TFindTimesResponse> {
    return this.post(`/api/workspaces/${slug}/find-available-times/`, data);
  }

  /**
   * Add a session (an event child) to a workspace event. Project events must
   * use the project-scoped sub-work-items endpoint (the workspace-scoped one
   * rejects project work items so project RBAC applies); project-less events
   * keep the workspace-scoped path. Answers the event's sessions in the
   * ``{sub_issues, state_distribution}`` envelope. The sessions are read as the
   * event's children (`queries/work-item-sub-issues.ts`).
   */
  async createEventSession(
    slug: string,
    eventId: string,
    data: TEventSessionPayload,
    projectId?: string | null,
  ): Promise<TIssue[]> {
    // The workspace endpoint stamps `category: "event"` server-side but the
    // project-scoped one does not — send it explicitly so both create events.
    const res = await this.post<TSubWorkItemsResponse>(
      projectId
        ? `/api/workspaces/${slug}/projects/${projectId}/work-items/${eventId}/sub-work-items/`
        : `/api/workspaces/${slug}/work-items/${eventId}/sub-work-items/`,
      { ...data, category: "event" },
    );
    return res.sub_issues;
  }

  /**
   * Update a session (event child). A session is itself a work item, patched
   * through the project-scoped work-item detail route when its event belongs
   * to a project, or the workspace-scoped one otherwise.
   */
  async updateEventSession(
    slug: string,
    sessionId: string,
    data: Partial<TEventSessionPayload>,
    projectId?: string | null,
  ): Promise<TIssue> {
    return this.patch(
      projectId
        ? `/api/workspaces/${slug}/projects/${projectId}/work-items/${sessionId}/`
        : `/api/workspaces/${slug}/work-items/${sessionId}/`,
      data,
    );
  }
}
